import { useUserStore } from '../../store/useUserStore';
import { useAnalysisStore } from '../../store/useAnalysisStore';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { styled, Box, useMediaQuery, useTheme } from '@mui/material';
import Url from '../../data/url';
import { BOTTOM_NAVI_V2, VIDEO_RECORD_TYPE, ANALYSIS_STATUS } from '../../data/define';
import { useKidDetailQuery } from '../../queries';
import Backdrop from '@mui/material/Backdrop';
import { useTranslation } from '../../hook/useTranslation';
import IcBar from '../../assets/image/navigation/ic_navi_bar.svg';
import IcHomeOn from '../../assets/image/navigation/ic_navi_home_on.svg';
import IcHomeOff from '../../assets/image/navigation/ic_navi_home_off.svg';
import IcReportOn from '../../assets/image/navigation/ic_navi_report_on.svg';
import IcReportOff from '../../assets/image/navigation/ic_navi_report_off.svg';
import IcEducationOn from '../../assets/image/navigation/ic_navi_edu_on.svg';
import IcEducationOff from '../../assets/image/navigation/ic_navi_edu_off.svg';
import IcMyOn from '../../assets/image/navigation/ic_navi_my_on.svg';
import IcMyOff from '../../assets/image/navigation/ic_navi_my_off.svg';
import IcCameraOff from '../../assets/image/navigation/ic_navi_camera_off.svg';
import IcCameraOn from '../../assets/image/navigation/ic_navi_camera_on.svg';
import IcNormanVideo from '../../assets/image/navigation/ic_btn_1.svg';
import IcQuestionareVideo from '../../assets/image/navigation/ic_btn_2.svg';
import IcHistoryVideo from '../../assets/image/navigation/ic_btn_3.svg';
import OneBtnAlert from './OneBtnAlert';

const RootContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: '20px',
  maxHeight: '20px',
  minHeight: '20px',
  backgroundColor: 'transparent',
  borderRadius: '10px 10px 0 0',
  boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.05)',
  minWidth: '300px',
});

const focusTextStyle: React.CSSProperties = {
  color: '#5EBEBB',
};
const textStyle: React.CSSProperties = {
  color: '#969696',
};
const VIDEO_LIMIT = 12;

interface BottomNavigationV2Props {
  focusTab?: number;
  isOpenToggleBtn?: boolean | null;
  setIsOpenToggleBtn?: (value: boolean) => void;
  currentReportVideoCount?: number | null;
}

export default function BottomNavigationV2({
  focusTab = 0,
  isOpenToggleBtn = null,
  setIsOpenToggleBtn = () => {},
  currentReportVideoCount = null,
}: BottomNavigationV2Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isUnder375 = useMediaQuery(theme.breakpoints.down(500));
  const user = useUserStore();
  const analysisState = useAnalysisStore((s) => s.analysisResult);
  const [open, setOpen] = useState(false);
  const isDraftMode = !user.isLoggedIn;

  const { data: kidDetail } = useKidDetailQuery(user?.mainKid?.id, {
    enabled: !isDraftMode && !!user?.mainKid?.id,
  });
  const reports = kidDetail?.reports || [];
  const [analysisProgressAlertOpen, setAnalysisProgressAlertOpen] = useState(false);
  const [kidRegistrationAlertOpen, setKidRegistrationAlertOpen] = useState(false);
  const [videoLimitExceededAlertOpen, setVideoLimitExceededAlertOpen] = useState(false);

  const notCompletedReport = reports?.find((r) => !r.isSuccess);

  useEffect(() => {
    if (isOpenToggleBtn === null) return;
    if (isOpenToggleBtn) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isOpenToggleBtn]);

  useEffect(() => {
    if (!open) {
      setIsOpenToggleBtn(false);
    }
  }, [open, setIsOpenToggleBtn]);

  const homeSx = focusTab === BOTTOM_NAVI_V2.HOME ? focusTextStyle : textStyle;
  const reportSx = focusTab === BOTTOM_NAVI_V2.REPORT ? focusTextStyle : textStyle;
  const educationSx = focusTab === BOTTOM_NAVI_V2.EDUCATION ? focusTextStyle : textStyle;
  const mySx = focusTab === BOTTOM_NAVI_V2.MY ? focusTextStyle : textStyle;

  let reportMenuLink = Url.Report;
  if (isDraftMode) {
    reportMenuLink = Url.Login;
  } else {
    reportMenuLink = user?.kids?.length < 1 ? Url.Home : Url.Report;
  }

  const handleDailyVideoAnalysis = useCallback(() => {
    if (user?.kids?.length === 0) {
      setKidRegistrationAlertOpen(true);
      return;
    }
    if (currentReportVideoCount === VIDEO_LIMIT) {
      setVideoLimitExceededAlertOpen(true);
      return;
    }
    if (analysisState.status === ANALYSIS_STATUS.PENDING) {
      setAnalysisProgressAlertOpen(true);
    } else {
      navigate(`${Url.KidsRegisterUpload}?type=${encodeURIComponent(VIDEO_RECORD_TYPE.DAILY)}`, {
        state: notCompletedReport
          ? { kidId: user?.mainKid?.id, mi: [], reportId: notCompletedReport?.id }
          : { kidId: user?.mainKid?.id, mi: [] },
      });
    }
  }, [navigate, user, analysisState, notCompletedReport, isDraftMode, currentReportVideoCount]);

  const handleQuestionVideoAnalysis = useCallback(() => {
    if (user?.kids?.length === 0) {
      setKidRegistrationAlertOpen(true);
      return;
    }
    if (currentReportVideoCount === VIDEO_LIMIT) {
      setVideoLimitExceededAlertOpen(true);
      return;
    }
    if (analysisState.status === ANALYSIS_STATUS.PENDING) {
      setAnalysisProgressAlertOpen(true);
    } else {
      navigate(`${Url.KidsRegisterUpload}?type=${encodeURIComponent(VIDEO_RECORD_TYPE.QNA)}`, {
        state: notCompletedReport
          ? {
              kidId: user?.mainKid?.id,
              mi: [],
              reportId: notCompletedReport?.id,
            }
          : {
              kidId: user?.mainKid?.id,
              mi: [],
            },
      });
    }
  }, [navigate, user, analysisState, notCompletedReport, isDraftMode, currentReportVideoCount]);

  const handleAnalysisClick = useCallback(() => {
    if (isDraftMode) {
      navigate(Url.Login);
    } else {
      setOpen(!open);
    }
  }, [navigate, isDraftMode, open]);

  return (
    <RootContainer>
      {/* Curved Bar Image */}
      <div className="z-50 w-full h-[60px] min-w-[300px] fixed bottom-0 left-0 right-0 flex items-center justify-center">
        <div
          className="h-[89px] rounded-[10px_0_0_0] bg-[#fff] absolute top-0 left-0"
          style={{ width: isUnder375 ? '30%' : '38%' }}
        />
        <img
          src={IcBar}
          className="absolute top-0 left-[50%] translate-x-[-50%] min-w-[360px]"
          alt="navigationBar"
        />
        <div
          className="h-[89px] rounded-[0_10px_0_0] bg-[#fff] absolute top-0 right-0"
          style={{ width: isUnder375 ? '30%' : '38%' }}
        />
      </div>
      {/* Circle Btn */}
      <div className="z-100 w-[80px] min-w-[80px] h-[80px] rounded-full flex flex-col items-center justify-center gap-[1px] absolute top-[-90px] left-[50%] translate-x-[-50%]">
        {/* 일상영상 버튼 */}
        <button
          type="button"
          style={
            {
              opacity: open ? 1 : 0,
              transform: open ? 'scale(1)' : 'scale(0.15)',
              transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
              pointerEvents: open ? 'auto' : 'none',
              background: 'linear-gradient(142deg, #5FCAC7 4.15%, #58BDBA 84.75%)',
              boxShadow:
                '-1px -4px 6.2px 0 rgba(0, 0, 0, 0.06) inset, 0 0 15px 0 rgba(0, 0, 0, 0.10)',
            } as React.CSSProperties
          }
          className="z-100 w-[80px] h-[80px] rounded-full flex flex-col items-center justify-center absolute bottom-[45px] left-[-45%] translate-x-[-50%]"
          onClick={handleDailyVideoAnalysis}
        >
          <img src={IcNormanVideo} alt="IcNormanVideo" />
          <span className="text-[13px] font-[700] text-[#fff]">{t('nav.daily_video')}</span>
        </button>
        {/* 문답영상 버튼 */}
        <button
          type="button"
          style={
            {
              opacity: open ? 1 : 0,
              transform: open ? 'scale(1)' : 'scale(0.15)',
              transition: open
                ? 'opacity 0.3s ease-in-out 0.1s, transform 0.3s ease-in-out'
                : 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
              pointerEvents: open ? 'auto' : 'none',
              background: 'linear-gradient(142deg, #5FCAC7 4.15%, #58BDBA 84.75%)',
              boxShadow:
                '-1px -4px 6.2px 0 rgba(0, 0, 0, 0.06) inset, 0 0 15px 0 rgba(0, 0, 0, 0.10)',
            } as React.CSSProperties
          }
          className="z-100 w-[80px] h-[80px] rounded-full flex flex-col items-center justify-center absolute bottom-[90px] left-[50%] translate-x-[-50%]"
          onClick={handleQuestionVideoAnalysis}
        >
          <img src={IcQuestionareVideo} alt="IcQuestionareVideo" />
          <span className="text-[13px] font-[700] text-[#fff]">{t('nav.qna_video')}</span>
        </button>
        {/* 성장기록 버튼 */}
        <button
          type="button"
          style={
            {
              opacity: open ? 1 : 0,
              transform: open ? 'scale(1)' : 'scale(0.15)',
              transition: open
                ? 'opacity 0.3s ease-in-out 0.2s, transform 0.3s ease-in-out'
                : 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
              pointerEvents: open ? 'auto' : 'none',
              background: 'linear-gradient(142deg, #5FCAC7 4.15%, #58BDBA 84.75%)',
              boxShadow:
                '-1px -4px 6.2px 0 rgba(0, 0, 0, 0.06) inset, 0 0 15px 0 rgba(0, 0, 0, 0.10)',
            } as React.CSSProperties
          }
          className="z-100 w-[80px] h-[80px] rounded-full flex flex-col items-center justify-center absolute bottom-[45px] right-[-145%] translate-x-[-50%]"
          onClick={() => navigate(Url.History)}
        >
          <img src={IcHistoryVideo} alt="IcHistoryVideo" />
          <span className="text-[13px] font-[700] text-[#fff]">{t('nav.growth_record')}</span>
        </button>
        {/* trigger btn */}
        <button
          type="button"
          style={
            {
              border: open ? '4px solid #5EBEBB' : 'none',
              background: open ? '#fff' : 'linear-gradient(142deg, #5FCAC7 4.15%, #58BDBA 84.75%)',
              boxShadow:
                '-1px -4px 6.2px 0 rgba(0, 0, 0, 0.06) inset, 0 0 15px 0 rgba(0, 0, 0, 0.10)',
            } as React.CSSProperties
          }
          className="z-100 w-[80px] h-[80px] rounded-full flex flex-col items-center justify-center gap-[1px] absolute bottom-0 left-[50%] translate-x-[-50%]"
          onClick={handleAnalysisClick}
        >
          <img src={open ? IcCameraOn : IcCameraOff} alt="camera" />
          <span
            className="text-[13px] font-[700] text-[#fff]"
            style={{ color: open ? '#5EBEBB' : '#fff' }}
          >
            {open ? t('nav.cancel') : t('nav.talent_analysis')}
          </span>
        </button>
      </div>
      <Backdrop sx={{ zIndex: 40 }} open={open} onClick={() => setOpen(false)} />
      {/* Navigation */}
      <div className="z-50 w-full min-w-[300px] fixed bottom-0 left-0 right-0 flex items-center justify-evenly">
        <Link
          className="my-[8px] w-[50px] min-w-[50px] flex flex-col items-center justify-end"
          to={isDraftMode ? Url.Home : Url.Root}
        >
          <img
            src={focusTab === BOTTOM_NAVI_V2.HOME ? IcHomeOn : IcHomeOff}
            alt="home"
            className="w-[24px] h-[24px] shrink-0"
          />
          <div
            className="font-[700] text-[#969696] text-[11px]"
            style={{
              ...homeSx,
            }}
          >
            {t('nav.home')}
          </div>
        </Link>
        <Link
          className="flex my-[8px] w-[50px] min-w-[50px] flex-col items-center justify-end"
          // to={isDraftMode ? Url.Login : Url.Report}
          to={reportMenuLink}
          onClick={(e) => {
            if (
              !isDraftMode &&
              (!user?.kids || user?.kids?.length === 0 || reports?.length === 0)
            ) {
              e.preventDefault();
              setKidRegistrationAlertOpen(true);
            }
          }}
        >
          <img
            src={focusTab === BOTTOM_NAVI_V2.REPORT ? IcReportOn : IcReportOff}
            alt="report"
            className="w-[24px] h-[24px] shrink-0"
          />
          <div
            className="font-[700] text-[#969696] text-[11px]"
            style={{
              ...reportSx,
            }}
          >
            {t('nav.report')}
          </div>
        </Link>
        <div className="w-[80px] bg-transparent h-full" />
        <Link
          className="flex my-[8px] w-[50px] min-w-[50px] flex-col items-center justify-end"
          to={isDraftMode ? Url.Login : Url.Education}
        >
          <img
            src={focusTab === BOTTOM_NAVI_V2.EDUCATION ? IcEducationOn : IcEducationOff}
            alt="education"
            className="w-[24px] h-[24px] shrink-0"
          />
          <div
            className="font-[700] text-[#969696] text-[11px]"
            style={{
              ...educationSx,
            }}
          >
            {t('nav.education')}
          </div>
        </Link>
        <Link
          className="my-[8px] w-[50px] min-w-[50px] flex flex-col items-center justify-end"
          to={isDraftMode ? Url.Login : Url.My}
        >
          <img
            src={focusTab === BOTTOM_NAVI_V2.MY ? IcMyOn : IcMyOff}
            alt="my"
            className="w-[24px] h-[24px] shrink-0"
          />
          <div
            className="font-[700] text-[#969696] text-[11px]"
            style={{
              ...mySx,
            }}
          >
            {t('nav.my')}
          </div>
        </Link>
      </div>
      <OneBtnAlert
        open={analysisProgressAlertOpen}
        onClose={() => setAnalysisProgressAlertOpen(false)}
        title={t('nav.alert.analysis_in_progress.title')}
        description={t('nav.alert.analysis_in_progress.description')}
        btnText={t('common.confirm')}
        onClick={() => setAnalysisProgressAlertOpen(false)}
      />
      <OneBtnAlert
        open={kidRegistrationAlertOpen}
        onClose={() => setKidRegistrationAlertOpen(false)}
        title={t('nav.alert.no_report.title')}
        description={t('nav.alert.no_report.description')}
        btnText={t('nav.alert.no_report.btn')}
        onClick={() => {
          setKidRegistrationAlertOpen(false);
          setOpen(true);
        }}
      />
      <OneBtnAlert
        open={videoLimitExceededAlertOpen}
        onClose={() => setVideoLimitExceededAlertOpen(false)}
        title={t('nav.alert.video_limit.title')}
        description={t('nav.alert.video_limit.description')}
        btnText={t('common.confirm')}
        onClick={() => setVideoLimitExceededAlertOpen(false)}
      >
        <div className="flex flex-col gap-[8px] mt-4">
          <div className="bg-[#ffeff6] rounded-[8px] p-[8px] flex gap-[8px] items-start">
            <div className="bg-white rounded-[100px] w-[24px] h-[24px] flex items-center justify-center shrink-0">
              <span className="font-['Pretendard',sans-serif] font-semibold text-[12px] text-[#f29723] leading-none tracking-[-0.24px]">
                1
              </span>
            </div>
            <div className="flex flex-col gap-[5px] flex-1">
              <div className="font-['Pretendard',sans-serif] font-bold text-[14px] text-[#363636] leading-[1.6]">
                {t('nav.alert.video_limit.step1_title')}
              </div>
              <div className="font-['Pretendard',sans-serif] font-normal text-[14px] text-[#363636] leading-[1.6]">
                {t('nav.alert.video_limit.step1_desc')}
              </div>
            </div>
          </div>
          <div className="bg-[#ffeff6] rounded-[8px] p-[8px] flex gap-[8px] items-start">
            <div className="bg-white rounded-[100px] w-[24px] h-[24px] flex items-center justify-center shrink-0">
              <span className="font-['Pretendard',sans-serif] font-semibold text-[12px] text-[#f29723] leading-none tracking-[-0.24px]">
                2
              </span>
            </div>
            <div className="flex flex-col gap-[5px] flex-1">
              <div className="font-['Pretendard',sans-serif] font-bold text-[14px] text-[#363636] leading-[1.6]">
                {t('nav.alert.video_limit.step2_title')}
              </div>
              <div className="font-['Pretendard',sans-serif] font-normal text-[14px] text-[#363636] leading-[1.6]">
                {t('nav.alert.video_limit.step2_desc')}
              </div>
            </div>
          </div>
        </div>
      </OneBtnAlert>
    </RootContainer>
  );
}
